import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

type TermsSection = {
  heading: string;
  paragraphs: string[];
};

type TermsContent = {
  homeLabel: string;
  title: string;
  version: string;
  sections: TermsSection[];
};

const TERMS_CONTENT: { pl: TermsContent; en: TermsContent } = {
  pl: {
    homeLabel: 'Strona glowna',
    title: 'Regulamin swiadczenia uslug Alenis (SaaS B2B, OCR/AI)',
    version: 'Wersja 1.3 | Data obowiazywania: 2026-03-07',
    sections: [
      {
        heading: '1. Postanowienia ogolne',
        paragraphs: [
          'Niniejszy Regulamin okresla zasady korzystania z platformy Alenis swiadczonej w modelu SaaS B2B, obejmujacej funkcje OCR, ekstrakcji danych, klasyfikacji dokumentow i analiz AI. Regulamin ma zastosowanie do przedsiebiorcow oraz osob dzialajacych w ich imieniu.',
          'Usluga jest przeznaczona wylacznie do zastosowan profesjonalnych i biznesowych. Korzystanie z uslug oznacza akceptacje Regulaminu. Zasady przetwarzania danych osobowych opisano takze w Polityce RODO pod adresem /rodo.',
          'Wylaczone jest stosowanie przepisow konsumenckich. Jezeli dostep do uslugi uzyskuje osoba fizyczna, oswiadcza ona, ze dziala w zwiazku z prowadzona dzialalnoscia gospodarcza lub zawodowa.',
        ],
      },
      {
        heading: '2. Zakres uslug',
        paragraphs: [
          'Alenis udostepnia narzedzia do przesylania dokumentow, ich automatycznego przetwarzania i generowania wynikow przy wykorzystaniu technologii OCR/AI. Administrator moze modyfikowac funkcjonalnosci, parametry i interfejs uslugi, jezeli jest to uzasadnione utrzymaniem, rozwojem, bezpieczenstwem albo zgodnoscia prawna.',
          'O ile strony nie postanowia inaczej w odrebnej umowie, usluga swiadczona jest w modelu "as is" i "as available".',
          'Alenis nie udziela jakichkolwiek gwarancji umownych, domniemanych ani ustawowych ponad bezwzglednie obowiazujace przepisy prawa, w szczegolnosci co do ciaglosci dzialania, dostepnosci, kompletnosci, interoperacyjnosci, bezblednosci i przydatnosci do okreslonego celu biznesowego Klienta.',
        ],
      },
      {
        heading: '3. Obowiazki uzytkownika',
        paragraphs: [
          'Uzytkownik zobowiazuje sie przekazywac wylacznie dane, do ktorych przetwarzania jest uprawniony, oraz nie dostarczac tresci bezprawnych, naruszajacych prawa osob trzecich ani danych objetych szczegolnymi ograniczeniami bez wymaganych podstaw prawnych i srodkow technicznych.',
          'Uzytkownik ponosi pelna odpowiedzialnosc za legalnosc danych wejsciowych, poprawnosc merytoryczna dokumentow, konfiguracje procesow biznesowych oraz wykorzystanie wynikow uzyskanych z systemu.',
          'Uzytkownik odpowiada za bezpieczenstwo swoich kont, hasel, tokenow i urzadzen koncowych. Utrata tokenow szyfrowania po stronie uzytkownika nie stanowi podstawy odpowiedzialnosci Alenis.',
          'Klient ponosi pelna odpowiedzialnosc za skutki operacyjne, finansowe, podatkowe, ksiegowe, prawne i regulacyjne wynikajace z wykorzystania uslugi i jej rezultatow.',
        ],
      },
      {
        heading: '4. Dane osobowe i role stron',
        paragraphs: [
          'Co do zasady Klient pozostaje administratorem danych osobowych wprowadzanych do uslugi, a Alenis dziala jako podmiot przetwarzajacy w zakresie okreslonym funkcjonalnoscia platformy i uzgodnieniami stron.',
          'Jezeli charakter wspolpracy tego wymaga, strony zawieraja odrebna umowe powierzenia przetwarzania danych (DPA). Klient odpowiada za spelnienie obowiazkow informacyjnych i legalnosc powierzanych danych.',
          'Klient zobowiazuje sie zwolnic Alenis z odpowiedzialnosci i pokryc wszelkie uzasadnione koszty, roszczenia, odszkodowania, kary administracyjne i wydatki (w tym koszty obslugi prawnej) wynikajace z naruszenia przez Klienta prawa, praw osob trzecich, obowiazkow regulacyjnych lub postanowien Regulaminu.',
        ],
      },
      {
        heading: '5. Ograniczenie odpowiedzialnosci',
        paragraphs: [
          'W najszerszym zakresie dopuszczalnym przez prawo odpowiedzialnosc Alenis za szkody posrednie, utracone korzysci, utrate przychodow, utrate reputacji, utrate danych, przerwy w dzialalnosci oraz skutki decyzji podjetych na podstawie wynikow OCR/AI jest wylaczona.',
          'Alenis nie ponosi odpowiedzialnosci za decyzje biznesowe, ksiegowe, podatkowe, prawne lub operacyjne podjete na podstawie wynikow uslugi, w tym wynikow OCR/AI. Wyniki maja charakter wylacznie informacyjny i pomocniczy.',
          'Ograniczenia wskazane powyzej nie dotycza szkod wyrzadzonych umyslnie, o ile bezwzglednie obowiazujace przepisy prawa stanowia inaczej.',
          'Odpowiedzialnosc Alenis jest wylaczona rowniez w zakresie utraty, ujawnienia lub modyfikacji danych spowodowanych dzialaniem osob trzecich, bledna konfiguracja po stronie Klienta, przejeciem danych uwierzytelniajacych Klienta, dzialaniem zlosliwego oprogramowania, awaria laczy lub infrastruktury zewnetrznej oraz sila wyzsza.',
          'Jedynym i wylacznym srodkiem ochrony Klienta z tytulu nienalezytego dzialania uslugi, poza przypadkami bezwzglednie wymaganymi prawem, jest usuniecie nieprawidlowosci w rozsadnym terminie albo proporcjonalny zwrot oplat za okres rzeczywistej niedostepnosci, jesli przewiduje to odrebna umowa.',
        ],
      },
      {
        heading: '6. Charakter i jakosc wynikow OCR/AI',
        paragraphs: [
          'Wyniki OCR/AI maja charakter porady/wskazowki i moga zawierac bledy, niepelnosci lub niejednoznacznosci. Alenis nie gwarantuje bezblednosci, kompletnosci, przydatnosci handlowej ani przydatnosci do okreslonego celu.',
          'Klient jest zobowiazany do samodzielnej weryfikacji wynikow przez osobe odpowiedzialna przed ich uzyciem operacyjnym, ksiegowym, podatkowym, prawnym lub audytowym.',
        ],
      },
      {
        heading: '7. Dostepnosc i wsparcie',
        paragraphs: [
          'O ile odrebna umowa nie stanowi inaczej, Alenis nie zapewnia gwarantowanego poziomu SLA. Przerwy techniczne, serwisowe oraz aktualizacje moga czasowo ograniczac dostepnosc uslugi.',
          'Alenis moze czasowo zawiesic dzialanie uslugi lub dostep Klienta, jezeli wymaga tego bezpieczenstwo, przeciwdzialanie naduzyciom, ryzyko naruszenia prawa lub integralnosci systemu.',
          'Alenis nie odpowiada za skutki planowanych i awaryjnych prac utrzymaniowych, migracji srodowisk, aktualizacji modeli AI oraz ograniczen po stronie dostawcow infrastruktury lub uslug zewnetrznych.',
        ],
      },
      {
        heading: '8. Czas trwania, rozwiazanie i usuniecie danych',
        paragraphs: [
          'Klient moze zakonczyc korzystanie z uslugi zgodnie z zasadami planu/subskrypcji. Alenis moze rozwiazac lub zawiesic dostep w przypadku naruszenia Regulaminu, prawa albo zagrozenia bezpieczenstwa.',
          'Dane sa usuwane lub anonimizowane zgodnie z polityka retencji i obowiazkami prawnymi Alenis.',
          'Alenis moze zatrzymac dane w zakresie niezbednym do zabezpieczenia roszczen, realizacji obowiazkow prawnych, audytowych i bezpieczenstwa.',
        ],
      },
      {
        heading: '9. Postanowienia koncowe',
        paragraphs: [
          'Regulamin moze byc aktualizowany. Aktualna wersja jest publikowana na tej stronie.',
          'Roszczenia Klienta wobec Alenis wygasaja, jezeli nie zostana zgloszone na pismie w terminie 30 dni od dnia, w ktorym Klient dowiedzial sie o zdarzeniu stanowiacym podstawe roszczenia, nie pozniej jednak niz 6 miesiecy od dnia zdarzenia, o ile bezwzglednie obowiazujace przepisy nie stanowia inaczej.',
          'W sprawach nieuregulowanych stosuje sie przepisy prawa polskiego. W relacjach B2B spory beda rozstrzygane przez sad wlasciwy miejscowo dla siedziby Alenis, o ile przepisy bezwzglednie obowiazujace nie stanowia inaczej.',
          'W przypadku rozbieznosci pomiedzy Regulaminem a indywidualna umowa B2B zawarta z Klientem, pierwszenstwo maja postanowienia tej umowy.',
          'Kontakt: alenis.services@gmail.com',
        ],
      },
    ],
  },
  en: {
    homeLabel: 'Home',
    title: 'Terms of Service for Alenis Services (B2B SaaS, OCR/AI)',
    version: 'Version 1.3 | Effective date: 2026-03-07',
    sections: [
      {
        heading: '1. General Provisions',
        paragraphs: [
          'These Terms define the rules for using the Alenis platform provided in a B2B SaaS model, including OCR, data extraction, document classification, and AI analysis features. The Terms apply to business entities and persons acting on their behalf.',
          'The service is intended solely for professional and business use. Use of the services constitutes acceptance of these Terms. Rules for personal data processing are also described in the GDPR Policy available at /rodo.',
          'Consumer law provisions are excluded. If a natural person accesses the service, that person represents acting in connection with business or professional activity.',
        ],
      },
      {
        heading: '2. Scope of Services',
        paragraphs: [
          'Alenis provides tools for uploading documents, automated processing, and generation of results using OCR/AI technologies. The administrator may modify functionalities, parameters, and the service interface when justified by maintenance, development, security, or legal compliance.',
          'Unless otherwise agreed in a separate agreement, the service is provided on an "as is" and "as available" basis.',
          'Alenis provides no contractual, implied, or statutory warranties beyond mandatory law, including with respect to continuity, availability, completeness, interoperability, error-free operation, or fitness for a particular business purpose.',
        ],
      },
      {
        heading: '3. User Obligations',
        paragraphs: [
          'The user undertakes to provide only data they are authorized to process and not to provide unlawful content, content infringing third-party rights, or data subject to special restrictions without required legal bases and technical safeguards.',
          'The user bears full responsibility for legality of input data, substantive correctness of documents, configuration of business processes, and use of results obtained from the system.',
          'The user is responsible for security of accounts, passwords, tokens, and endpoint devices. Loss of encryption tokens on the user side does not create liability of Alenis.',
          'The client bears full responsibility for operational, financial, tax, accounting, legal, and regulatory consequences resulting from use of the service and its outputs.',
        ],
      },
      {
        heading: '4. Personal Data and Roles of the Parties',
        paragraphs: [
          'As a rule, the client remains the controller of personal data entered into the service, and Alenis acts as a processor to the extent defined by platform functionality and the parties\' arrangements.',
          'Where required by the nature of cooperation, the parties conclude a separate data processing agreement (DPA). The client is responsible for fulfilling information obligations and legality of entrusted data.',
          'The client undertakes to indemnify Alenis and cover all reasonable costs, claims, damages, administrative penalties, and expenses (including legal fees) resulting from the client\'s breach of law, third-party rights, regulatory obligations, or these Terms.',
        ],
      },
      {
        heading: '5. Limitation of Liability',
        paragraphs: [
          'To the fullest extent permitted by law, Alenis\' liability for indirect damages, lost profits, loss of revenue, loss of reputation, loss of data, business interruption, and consequences of decisions made based on OCR/AI outputs is excluded.',
          'Alenis is not liable for business, accounting, tax, legal, or operational decisions made based on service outputs, including OCR/AI outputs. Outputs are informational and supportive only.',
          'The above limitations do not apply to willful misconduct to the extent mandatory law provides otherwise.',
          'Alenis liability is also excluded for loss, disclosure, or modification of data caused by third parties, client-side misconfiguration, compromise of client credentials, malware, network outages, external infrastructure failures, or force majeure.',
          'The client\'s sole and exclusive remedy for improper service performance, except where mandatory law requires otherwise, is correction of irregularities within a reasonable period or a proportionate refund for actual unavailability, if provided in a separate agreement.',
        ],
      },
      {
        heading: '6. Nature and Quality of OCR/AI Outputs',
        paragraphs: [
          'OCR/AI outputs are advisory/guidance in nature and may contain errors, omissions, or ambiguities. Alenis does not guarantee accuracy, completeness, merchantability, or fitness for a particular purpose.',
          'The client must independently verify outputs through a responsible person before operational, accounting, tax, legal, or audit use.',
        ],
      },
      {
        heading: '7. Availability and Support',
        paragraphs: [
          'Unless otherwise specified in a separate agreement, Alenis does not provide a guaranteed SLA level. Technical/service interruptions and updates may temporarily limit availability.',
          'Alenis may temporarily suspend service operation or client access where required by security, abuse prevention, risk of legal non-compliance, or system integrity.',
          'Alenis is not responsible for consequences of planned or emergency maintenance, environment migrations, AI model updates, or limitations on the side of infrastructure or external service providers.',
        ],
      },
      {
        heading: '8. Term, Termination, and Data Deletion',
        paragraphs: [
          'The client may stop using the service according to plan/subscription rules. Alenis may terminate or suspend access in case of breach of these Terms, law, or security risk.',
          'Data is deleted or anonymized according to retention policy and legal obligations of Alenis.',
          'Alenis may retain data to the extent necessary to secure claims and fulfill legal, audit, and security obligations.',
        ],
      },
      {
        heading: '9. Final Provisions',
        paragraphs: [
          'These Terms may be updated. The current version is published on this page.',
          'Client claims against Alenis expire if not submitted in writing within 30 days from the date the client learned about the event giving rise to the claim, and no later than 6 months from the event date, unless mandatory law provides otherwise.',
          'Matters not regulated herein are governed by Polish law. In B2B relations, disputes shall be resolved by the court having local jurisdiction for Alenis\' registered office, unless mandatory law provides otherwise.',
          'In case of discrepancies between these Terms and an individual B2B agreement with the client, the provisions of that agreement prevail.',
          'Contact: alenis.services@gmail.com',
        ],
      },
    ],
  },
};

const RegulaminPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isPolish = i18n.language.toLowerCase().startsWith('pl');
  const content = isPolish ? TERMS_CONTENT.pl : TERMS_CONTENT.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-blue-900 text-white">
      <header className="relative z-10 bg-white/5 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Alenis</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <Link to="/" className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl">
                {content.homeLabel}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <article className="bg-white text-slate-900 rounded-2xl p-8 md:p-10 leading-7 space-y-6 shadow-2xl border border-slate-200">
          <header className="space-y-2">
            <h1 className="text-3xl font-bold">{content.title}</h1>
            <p className="text-sm text-slate-500">{content.version}</p>
          </header>

          {content.sections.map((section) => (
            <section className="space-y-3" key={section.heading}>
              <h2 className="text-xl font-semibold">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={`${section.heading}-${paragraph.slice(0, 24)}`}>
                  {paragraph.includes('/rodo') ? (
                    <>
                      {paragraph.replace('/rodo', '')}
                      <Link to="/rodo" className="text-blue-700 underline ml-1">/rodo</Link>
                    </>
                  ) : paragraph.includes('alenis.services@gmail.com') ? (
                    <>
                      {paragraph.replace('alenis.services@gmail.com', '')}
                      <a href="mailto:alenis.services@gmail.com" className="text-blue-700 underline">alenis.services@gmail.com</a>
                    </>
                  ) : (
                    paragraph
                  )}
                </p>
              ))}
            </section>
          ))}
        </article>
        </div>
      </main>
    </div>
  );
};

export default RegulaminPage;
